/**
 * @fileoverview Instructor sign-in / sign-up form — Google OAuth plus email.
 *
 * Renders as the Instructor tab INSIDE AuthPortalLayout, which owns the brand
 * mark, the portal tab strip, the heading and the "Back to home" link. This file
 * is the form and nothing else, so the three portals can't drift apart visually.
 *
 * It is also where an INVITATION lands. The email's button arrives here
 * carrying `?invite=` (a program) or `?org_invite=` (an organization), because
 * every in-app destination is signed-in-only and a signed-out click on one
 * renders nothing — which is how an invitation gets read as lost. The banner
 * names who is asking, `?signup=1` opens the form in Sign Up mode for someone
 * with no account yet, and the destination is stashed so it survives every
 * route in, Google's round trip included.
 *
 * `?signup=1` needs no invitation: it is also how the landing page's "Create
 * your studio" opens this form (see lib/authPortals). Plain `/login` stays Log
 * In, because it is what the portal tabs and every "Log in" or "Sign in" link
 * point at.
 */

import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";
import { AuthCard, AuthField, AuthSubmit, AuthNote, authLinkCls } from "@/components/auth/authUi";
import { inviteFromUrl, stashInvite, inviteLanding } from "@/lib/pendingInvite";
import { consumeReturnTo } from "@/lib/returnTo";

const GOOGLE_CLIENT_ID = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;

const InstructorLoginPage = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  // Read once, from the URL this page was ENTERED on. The router's search is
  // the source rather than window.location, so the value is the one this route
  // was mounted with.
  const [invite] = useState(() => inviteFromUrl(search));
  const [inviteFrom, setInviteFrom] = useState(null);
  // Sign Up mode is asked for by the URL — `?signup=1`, with or without an
  // invitation — and read once, like the invitation, from the search this
  // route was mounted with.
  const [isLoginMode, setIsLoginMode] = useState(() => new URLSearchParams(search).get("signup") !== "1");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", confirmPassword: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  // Inline field-level validation errors, surfaced on submit (in addition to
  // toasts for server-side failures).
  const [errors, setErrors] = useState({});

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Hold the invitation for after sign-in, and name who is asking. The lookup
  // is public and deliberately thin (org, program title, whether the address
  // has an account) — it renders before anyone is signed in, so it cannot ask
  // an authenticated endpoint. A failure costs the banner, never the sign-in.
  useEffect(() => {
    if (!invite) return;
    stashInvite(invite);
    if (!invite.inviteId) return;
    let live = true;
    axios
      .get(`${API}/marketplace/invites/${invite.inviteId}/preview`)
      .then((res) => live && setInviteFrom(res.data))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [invite]);

  /**
   * Where to go once they are in — the invitation's page, or the usual one. An
   * account that still owes onboarding goes the usual way (AuthProvider turns
   * it to /onboarding) and the invitation waits for onboarding's last step.
   */
  const landing = (user, fallback) => inviteLanding(user) || fallback;

  const handleGoogleLogin = () => {
    // In Sign Up mode the ToS checkbox is required and gates this button.
    if (!isLoginMode && !tosAccepted) {
      setErrors((p) => ({ ...p, tos: "Please accept the Terms of Service to continue" }));
      return;
    }
    // The Google redirect leaves the page, so stash the acceptance for the OAuth
    // callback (LandingPage) to forward to the backend. Stashed in BOTH modes
    // because "Continue with Google" doubles as sign-up: a brand-new account
    // created from Log In mode must still record ToS acceptance (the disclosure
    // note under the button is the conspicuous notice). The backend only records
    // it when the account is new, so a returning user's login is unaffected.
    sessionStorage.setItem("hans_tos_accepted", "1");
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: window.location.origin,
      response_type: "code",
      scope: "email profile",
      prompt: "select_account",
      access_type: "online",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!EMAIL_RE.test(form.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }
    // Signup enforces a stronger 8-character minimum; login keeps 6 so
    // existing accounts created under the old policy can still sign in.
    const minPasswordLength = isLoginMode ? 6 : 8;
    if (!form.password) {
      nextErrors.password = "Password is required";
    } else if (form.password.length < minPasswordLength) {
      nextErrors.password = `Password must be at least ${minPasswordLength} characters`;
    }
    if (!isLoginMode && form.password !== form.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }
    if (!isLoginMode && !tosAccepted) {
      nextErrors.tos = "Please accept the Terms of Service to continue";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      if (nextErrors.confirmPassword && !nextErrors.email && !nextErrors.password) {
        toast.error("Passwords do not match");
      }
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const endpoint = isLoginMode ? "/auth/login" : "/auth/signup";
      const payload = isLoginMode
        ? { email: form.email, password: form.password }
        : {
            email: form.email,
            password: form.password,
            first_name: form.first_name,
            last_name: form.last_name,
            tos_accepted: true,
          };
      const result = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      // Email/password signup no longer logs in directly — it requires email
      // verification first.
      if (!isLoginMode && result.data.verification_required) {
        navigate("/verify-email", { state: { email: result.data.email || form.email } });
        return;
      }
      const user = result.data.user;
      if (isLoginMode) {
        // A Founding Ensemble invitation for an account that already exists is
        // redeemed at sign-in, so it can be claimed right here by somebody who
        // read the invitation days ago and came in through the front door. Open
        // the founding welcome when it was, rather than changing their plan
        // underneath them with no explanation.
        navigate(landing(user, consumeReturnTo() || "/dashboard"), {
          state: { user, ...(result.data.founding_claimed === true ? { welcome: true } : {}) },
        });
      } else {
        navigate(landing(user, "/onboarding"), { state: { user } });
      }
      const greeting = (user.first_name || user.name?.split(/\s+/)[0] || "").trim();
      toast.success(greeting ? `Welcome, ${greeting}!` : "Welcome!");
    } catch (error) {
      // An unverified account is bounced to the verification screen (the backend
      // has already re-sent a fresh code).
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && detail?.code === "email_not_verified") {
        navigate("/verify-email", { state: { email: detail.email || form.email } });
        return;
      }
      toast.error(getErrorMessage(error, "Authentication failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmMismatch = form.confirmPassword && form.password !== form.confirmPassword;

  return (
    <div>
      {/* Who is asking, and what signing in will do. An invitation that does
          not say whose it is reads as a mis-sent email. */}
      {invite && (
        <div
          data-testid="invite-banner"
          className="mb-4 rounded-[14px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3"
        >
          <p className="font-sans text-[14px] text-[var(--text-primary)]">
            {inviteFrom ? (
              <>
                <strong>{inviteFrom.org_name}</strong> invited you to teach{" "}
                <strong>{inviteFrom.program_title}</strong>.
              </>
            ) : (
              "You have an invitation waiting."
            )}
          </p>
          <p className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">
            {isLoginMode
              ? "Sign in and it's under Programs → Invites."
              : "Create your account and it's under Programs → Invites."}
          </p>
        </div>
      )}

      {/* Google OAuth */}
      <Button
        onClick={handleGoogleLogin}
        className="mb-4 w-full rounded-full bg-[var(--text-primary)] py-3 text-sm font-medium
                   text-[var(--studio-base)] transition-transform hover:scale-105 active:scale-95"
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </Button>

      {/* Terms notice for the Google path in Log In mode — the OAuth button
          doubles as sign-up, and Log In mode shows no ToS checkbox, so this is
          the conspicuous acceptance notice for a brand-new Google account. */}
      {isLoginMode && (
        <AuthNote className="-mt-2 mb-4 text-center" data-testid="google-tos-notice">
          By continuing with Google, you agree to our{" "}
          <Link to="/terms" target="_blank" rel="noopener" className={authLinkCls}>
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" rel="noopener" className={authLinkCls}>
            Privacy Policy
          </Link>
        </AuthNote>
      )}

      {/* Divider */}
      <div className="mb-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-[var(--studio-divider-strong)]" />
        <span className="text-xs text-[var(--text-tertiary)]">or</span>
        <div className="h-px flex-1 bg-[var(--studio-divider-strong)]" />
      </div>

      {/* Email form */}
      <form onSubmit={handleEmailSubmit} noValidate className="space-y-4">
        <AuthCard>
          {!isLoginMode && (
            <div className="grid grid-cols-2 gap-3">
              <AuthField
                label="First Name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                required
                placeholder="First name"
                autoComplete="given-name"
              />
              <AuthField
                label="Last Name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Last name"
                autoComplete="family-name"
              />
            </div>
          )}
          <AuthField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
            required
            placeholder="your@email.com"
            autoComplete="email"
            error={errors.email}
            errorTestId="email-error"
          />
          <AuthField
            label="Password"
            type="password"
            reveal
            value={form.password}
            onChange={(e) => { setForm({ ...form, password: e.target.value }); if (errors.password) setErrors((p) => ({ ...p, password: undefined })); }}
            required
            minLength={isLoginMode ? 6 : 8}
            placeholder={isLoginMode ? "Your password" : "At least 8 characters"}
            autoComplete={isLoginMode ? "current-password" : "new-password"}
            error={errors.password}
            errorTestId="password-error"
            hint={!isLoginMode ? "Use at least 8 characters, with a mix of letters, numbers, and symbols." : undefined}
            hintTestId="password-hint"
          >
            {isLoginMode && (
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className={`text-xs ${authLinkCls}`}>
                  Forgot password?
                </Link>
              </div>
            )}
          </AuthField>
          {!isLoginMode && (
            <AuthField
              label="Confirm Password"
              type="password"
              reveal
              revealSubject="confirm password"
              value={form.confirmPassword}
              onChange={(e) => { setForm({ ...form, confirmPassword: e.target.value }); if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: undefined })); }}
              required
              minLength={8}
              placeholder="Confirm your password"
              autoComplete="new-password"
              /* Live feedback as the user types wins over the on-submit error. */
              error={confirmMismatch ? "Passwords do not match" : (!form.confirmPassword ? errors.confirmPassword : undefined)}
              errorTestId="confirm-password-error"
            >
              {form.confirmPassword && !confirmMismatch && (
                <p className="mt-1 text-xs text-[var(--state-success)]" data-testid="confirm-password-match">
                  Passwords match
                </p>
              )}
            </AuthField>
          )}
          {!isLoginMode && (
            <div>
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={tosAccepted}
                  onChange={(e) => { setTosAccepted(e.target.checked); if (errors.tos) setErrors((p) => ({ ...p, tos: undefined })); }}
                  aria-invalid={errors.tos ? "true" : undefined}
                  className="mt-0.5 accent-[var(--accent-yellow)]"
                  data-testid="tos-checkbox"
                />
                <AuthNote as="span">
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noopener" className={authLinkCls}>
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" rel="noopener" className={authLinkCls}>
                    Privacy Policy
                  </Link>
                </AuthNote>
              </label>
              {errors.tos && (
                <p className="mt-1 text-xs text-[var(--state-danger)]" data-testid="tos-error">{errors.tos}</p>
              )}
            </div>
          )}
        </AuthCard>

        <AuthSubmit submitting={submitting} icon={Mail}>
          {isLoginMode ? "Log In" : "Sign Up"}
        </AuthSubmit>
      </form>

      <div className="mt-6 text-center">
        <AuthNote>
          {isLoginMode ? (
            <>Need an account?{" "}
              <button onClick={() => setIsLoginMode(false)} className={authLinkCls}>
                Sign up
              </button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button onClick={() => setIsLoginMode(true)} className={authLinkCls}>
                Log in
              </button>
            </>
          )}
        </AuthNote>
      </div>
    </div>
  );
};

export default InstructorLoginPage;
